import { TreeNode } from './tree-node';

export interface ReorderModeState {
    parentId: string;
    parentPath: string;
    parentCode: string;
    expandedNodePaths: string[];
    enAttente: boolean;
}

export interface LigneEnfantReordonnable {
    idEnfant: string;
    hautY: number;
    milieuY: number;
    basY: number;
}

export interface RepereInsertionOrdre {
    idNoeud: string;
    position: 'before' | 'after';
}

export interface ProjectionOrdreEnfants {
    nouvelOrdreEnfants: string[];
    repereInsertion: RepereInsertionOrdre | null;
}

const MARGE_ZONE_REORDONNANCEMENT_PX = 8;

export function compterEnfantsDirects(parent: TreeNode | null): number {
    return lireIdsEnfantsDirects(parent).length;
}

export function lireIdsEnfantsDirects(parent: TreeNode | null): string[] {
    return parent?.enfants.map(enfant => enfant.objetMaquette.id) ?? [];
}

export function estEnfantDirectDuParentReordonne(node: TreeNode, reorderMode: ReorderModeState | null): boolean {
    if (!reorderMode) {
        return false;
    }

    const prefix = `${reorderMode.parentPath}.`;
    if (!node.path.startsWith(prefix)) {
        return false;
    }

    return !node.path.substring(prefix.length).includes('.');
}

export type PositionOrdonnancementAction = 'debut' | 'fin' | 'precedent' | 'suivant';

export function calculerOrdreCibleDepuisAction(
    idsEnfantsActuels: string[],
    idEnfant: string,
    position: PositionOrdonnancementAction
): string[] | null {
    const indexCourant = idsEnfantsActuels.indexOf(idEnfant);
    if (indexCourant < 0) {
        return null;
    }

    const idsSansEnfantCible = idsEnfantsActuels.filter(id => id !== idEnfant);
    const indexInsertion = calculerIndexInsertionDepuisAction(position, indexCourant, idsSansEnfantCible.length);
    return insererEnfant(idsSansEnfantCible, idEnfant, indexInsertion);
}

function calculerIndexInsertionDepuisAction(position: PositionOrdonnancementAction, indexCourant: number, dernierIndex: number): number {
    switch (position) {
        case 'debut':
            return 0;
        case 'fin':
            return dernierIndex;
        case 'precedent':
            return Math.max(indexCourant - 1, 0);
        case 'suivant':
            return Math.min(indexCourant + 1, dernierIndex);
    }
}

export function calculerProjectionOrdreEnfants(
    idsEnfantsActuels: string[],
    idEnfantDeplace: string,
    positionYDrop: number,
    lignesEnfants: LigneEnfantReordonnable[]
): ProjectionOrdreEnfants | null {
    if (!idsEnfantsActuels.includes(idEnfantDeplace)) {
        return null;
    }

    const lignesEnfantsDirects = filtrerLignesEnfantsDirects(idsEnfantsActuels, lignesEnfants);
    if (!lignesEnfantsDirects.length || !estDansZoneReordonnable(positionYDrop, lignesEnfantsDirects)) {
        return null;
    }

    const lignesReferences = lignesEnfantsDirects.filter(ligne => ligne.idEnfant !== idEnfantDeplace);
    if (!lignesReferences.length) {
        return null;
    }

    const indexInsertionVisible = calculerIndexInsertionVisible(positionYDrop, lignesReferences);
    const repereInsertion = calculerRepereInsertion(lignesReferences, indexInsertionVisible);

    return {
        nouvelOrdreEnfants: insererEnfantDepuisRepere(idsEnfantsActuels, idEnfantDeplace, repereInsertion),
        repereInsertion
    };
}

export function aLeMemeOrdre(idsSource: string[], idsCibles: string[]): boolean {
    return idsSource.length === idsCibles.length && idsSource.every((id, index) => id === idsCibles[index]);
}

function calculerRepereInsertion(lignesReferences: LigneEnfantReordonnable[], indexInsertion: number): RepereInsertionOrdre {
    const position = indexInsertion <= 0 ? 'before' : 'after';
    const indexReference = position === 'before' ? 0 : Math.min(indexInsertion - 1, lignesReferences.length - 1);

    return {
        idNoeud: lignesReferences[indexReference].idEnfant,
        position
    };
}

function filtrerLignesEnfantsDirects(idsEnfantsActuels: string[], lignesEnfants: LigneEnfantReordonnable[]): LigneEnfantReordonnable[] {
    const idsEnfantsAutorises = new Set(idsEnfantsActuels);
    return lignesEnfants.filter(ligne => idsEnfantsAutorises.has(ligne.idEnfant)).sort((a, b) => a.milieuY - b.milieuY);
}

function estDansZoneReordonnable(positionYDrop: number, lignesEnfants: LigneEnfantReordonnable[]): boolean {
    const premiereLigne = lignesEnfants[0];
    const derniereLigne = lignesEnfants[lignesEnfants.length - 1];

    return (
        positionYDrop >= premiereLigne.hautY - MARGE_ZONE_REORDONNANCEMENT_PX &&
        positionYDrop <= derniereLigne.basY + MARGE_ZONE_REORDONNANCEMENT_PX
    );
}

function calculerIndexInsertionVisible(positionYDrop: number, lignesSansEnfantDeplace: LigneEnfantReordonnable[]): number {
    const indexInsertion = lignesSansEnfantDeplace.findIndex(ligne => positionYDrop < ligne.milieuY);
    return indexInsertion < 0 ? lignesSansEnfantDeplace.length : indexInsertion;
}

function insererEnfantDepuisRepere(idsEnfantsActuels: string[], idEnfantDeplace: string, repereInsertion: RepereInsertionOrdre): string[] {
    const idsSansEnfantDeplace = idsEnfantsActuels.filter(id => id !== idEnfantDeplace);
    const indexReference = idsSansEnfantDeplace.indexOf(repereInsertion.idNoeud);
    const indexInsertion = repereInsertion.position === 'before' ? indexReference : indexReference + 1;

    return insererEnfant(idsSansEnfantDeplace, idEnfantDeplace, indexInsertion);
}

function insererEnfant(idsEnfants: string[], idEnfant: string, indexInsertion: number): string[] {
    const nouvelOrdreEnfants = [...idsEnfants];
    nouvelOrdreEnfants.splice(indexInsertion, 0, idEnfant);
    return nouvelOrdreEnfants;
}
